import { Mutex } from 'async-mutex';
import { EnvironmentDetail, SmartInterval } from '@src/utils';
import { capture } from '@src/services';
import { IBarcodeScanResult, parseBarcodeScannerResult } from '@services/barcode/BarcodeScannerResult';

export type BarcodeScannerCallback = (response: IBarcodeScanResult) => void;

/**
 * This service is used to start/stop polling for customer token (QR code) from their devices.
 * It allows calling capture() from assistedShoppingBarcodeService.ts on an interval to pop any new scans that
 * may have been read. If hex profile is needed the function startHexProfile() can be called independently.
 *
 * If using together with components, checkout useBarcodeScanner from the pkg IhmAssociateToolsMeridianComponents
 * to automatically manage the start and stop of polling on component lifecycle.
 */
export class BarcodeScanner {
    // Variable to save the state of the singleton
    private isPolling = false;

    // Variable to make sure that stopPolling and startPolling arent called simultaneously
    private readonly mutex = new Mutex();

    private callback?: BarcodeScannerCallback;

    // Timer for polling
    private timer?: SmartInterval;

    // Time constants for the timer
    private static readonly INTERVAL_NAME = 'BARCODE_SCAN_POLL';
    private static readonly ONE_SECOND_IN_MILLISECONDS = 1000;
    private static readonly FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;

    // Singleton pattern.
    protected static instance: BarcodeScanner;

    protected constructor() {}

    public static getInstance = () => {
        if (!BarcodeScanner.instance) {
            BarcodeScanner.instance = new BarcodeScanner();
            console.debug('BarcodeScannerService initialized');
        }
        return BarcodeScanner.instance;
    };

    public setCallback = (callback: BarcodeScannerCallback) => {
        if (callback) {
            this.callback = callback;
        }
    };

    private poll = async (): Promise<void> => {
        if (typeof this.callback !== 'function') {
            // Make sure the callback is defined and a callback
            return;
        }

        let rawResponse: unknown;

        try {
            rawResponse = await capture();
        } catch (error) {
            // No scans will throw
            // https://tiny.amazon.com/v8dqqfs1/codeamazpackIhmCblobd5d2barc
            return;
        } finally {
            this.timer?.sendHeartbeat();
        }

        const response = parseBarcodeScannerResult(rawResponse);
        this.callback?.(response);
    };

    /**
     * Start polling for scans from mobile devices.
     */
    public startPolling = async (): Promise<void> => {
        if (!EnvironmentDetail.isAndroid()) {
            console.debug('Not running on Android. Possibly running on a desktop browser. Skipping barcode scan polling.');
            return;
        }

        if (!this.callback) {
            throw Error('Callback undefined. Use "setCallback" or  if using a meridian component "useBarcodeScanner"');
        }

        await this.mutex.runExclusive(async () => {
            if (this.isPolling) {
                console.debug('Attempted to start polling, but already polling.');
                return;
            }

            console.debug('Starting polling');

            this.timer = new SmartInterval(this.poll, BarcodeScanner.ONE_SECOND_IN_MILLISECONDS, BarcodeScanner.INTERVAL_NAME, BarcodeScanner.FIVE_MINUTES_IN_MILLISECONDS);

            this.isPolling = true;
        });
    };

    /**
     * Stop polling for scans from mobile devices.
     */
    public stopPolling = async (): Promise<void> => {
        await this.mutex.runExclusive(async () => {
            if (!this.isPolling) {
                console.debug('Attempted to stop polling, but not polling.');
                return;
            }

            console.debug('Stopping polling');

            if (this.timer) {
                this.timer.cancel();
            }

            this.isPolling = false;
        });
    };
}
